import { BarChart2, Layers, MessageSquare, MoreHorizontal, Play, TrendingUp, Type } from 'react-feather';
import { SegmentationDashboardWidget, type WidgetInstance } from '@trinity/chat';
import { DashboardChartTypeEnum, type DashboardChartType } from '@trinity/chat/types/dashboard-config';
import type { PaletteEntry } from '../PaletteContext.js';

/**
 * What the Add Widget page lists, and which of it a builder offers. A builder's palette entries
 * (`PaletteContext.tsx`) stay the single place that decides which kinds of widget it can add; this
 * module only maps those entries onto the page's categories, so every builder keeps its selection.
 *
 * Two modes share the categories:
 * - `library`: saved project widgets (with data) per kind, plus blank Text / Section cards;
 * - `new`: one template per widget type, picked before data is added.
 */
export type AddWidgetMode = 'library' | 'new';

export type AddWidgetCategoryId = 'chart' | 'kpi' | 'text' | 'quote' | 'media' | 'section' | 'more';

export type AddWidgetCategory = {
  id: AddWidgetCategoryId;
  label: string;
  icon: PaletteEntry['icon'];
  /** Cards in this category are wide (quotes and media carry a transcript clip). */
  wide?: boolean;
};

export const ADD_WIDGET_CATEGORIES: readonly AddWidgetCategory[] = [
  { id: 'chart', label: 'Charts', icon: BarChart2 },
  { id: 'text', label: 'Text', icon: Type },
  { id: 'kpi', label: 'KPI Card', icon: TrendingUp },
  { id: 'quote', label: 'Quotes', icon: MessageSquare, wide: true },
  { id: 'media', label: 'Media', icon: Play, wide: true },
  { id: 'section', label: 'Section', icon: Layers },
  { id: 'more', label: 'More', icon: MoreHorizontal },
];

// ─── Chart templates ──────────────────────────────────────────────────────────

type TemplateCopy = { label: string; description: string; needs: string; bestFor: string };

/** A record over every chart type: a new `DashboardChartType` is a type error until it is described here. */
const CHART_TEMPLATE_COPY: Record<DashboardChartType, TemplateCopy> = {
  bar: {
    label: 'Bar',
    description: 'Compare values across categories',
    needs: '1 dimension, 1 measure',
    bestFor: 'Brand or metric comparison',
  },
  'stacked-bar': {
    label: 'Stacked Bar',
    description: 'Show the parts that make up each bar',
    needs: '1 dimension, 1 measure, 1 split',
    bestFor: 'Segment mix by brand',
  },
  'multiple-clustered-bar': {
    label: 'Clustered Bar',
    description: 'Compare groups side by side',
    needs: '2 dimensions, 1 measure',
    bestFor: 'User vs non-user by region',
  },
  'multiple-stacked-bar': {
    label: 'Multi Stacked',
    description: 'Stack several measures per category',
    needs: '1 dimension, 2 or more measures',
    bestFor: 'Share of voice by channel',
  },
  pie: {
    label: 'Pie',
    description: 'Show each part as a share of the total',
    needs: '1 dimension, 1 measure',
    bestFor: 'Simple share, up to 5 parts',
  },
  line: {
    label: 'Line',
    description: 'Show a trend over time',
    needs: '1 time dimension, 1 measure',
    bestFor: 'Monthly Rx trend',
  },
  'multiple-line': {
    label: 'Multi Line',
    description: 'Compare several trends on one axis',
    needs: '1 time dimension, 1 measure, 1 split',
    bestFor: 'Adoption by cohort',
  },
  'data-table': {
    label: 'Data Table',
    description: 'Show exact values in rows and columns',
    needs: 'Any analysis output',
    bestFor: 'Detailed survey results',
  },
  'kpi-widget': {
    label: 'KPI Widget',
    description: 'One headline number with its change',
    needs: '1 measure, optional prior period',
    bestFor: 'HCPs reached, NPS',
  },
  'progress-bar-widget': {
    label: 'Progress Bar',
    description: 'Current value against a target',
    needs: '1 measure, 1 target',
    bestFor: 'Enrollment vs target',
  },
  'semi-circle-progress-widget': {
    label: 'Semi Circle Progress',
    description: 'Progress toward a goal on an arc',
    needs: '1 measure, 1 goal',
    bestFor: 'Awareness vs goal',
  },
};

/** The order templates are listed in (comparison, trend, table, then the KPI kinds). */
const CHART_TEMPLATE_ORDER: readonly DashboardChartType[] = [
  'bar',
  'stacked-bar',
  'multiple-clustered-bar',
  'multiple-stacked-bar',
  'pie',
  'line',
  'multiple-line',
  'data-table',
  'kpi-widget',
  'progress-bar-widget',
  'semi-circle-progress-widget',
];

/** Chart types that read as a single headline figure; they get their own KPI Card category. */
const KPI_CHART_TYPES: ReadonlySet<string> = new Set<DashboardChartType>([
  'kpi-widget',
  'progress-bar-widget',
  'semi-circle-progress-widget',
]);

export const isKpiChartType = (type: string | undefined): boolean => type !== undefined && KPI_CHART_TYPES.has(type);

/** The label of a chart type, or undefined for a type this builder doesn't know. */
export function chartTypeLabel(type: string | undefined): string | undefined {
  const parsed = DashboardChartTypeEnum.safeParse(type);
  return parsed.success ? CHART_TEMPLATE_COPY[parsed.data].label : undefined;
}

// ─── Templates (New widget mode) ──────────────────────────────────────────────

export type WidgetTemplate = TemplateCopy & {
  id: string;
  category: AddWidgetCategoryId;
  /** The palette type handed to `onSelect` (`chart` for every chart type). */
  type: string;
  /** Set for chart templates: the chart type the new widget starts as. */
  chartType?: DashboardChartType;
};

const chartTemplate = (chartType: DashboardChartType): WidgetTemplate => ({
  ...CHART_TEMPLATE_COPY[chartType],
  id: `chart.${chartType}`,
  category: isKpiChartType(chartType) ? 'kpi' : 'chart',
  type: 'chart',
  chartType,
});

export const WIDGET_TEMPLATES: readonly WidgetTemplate[] = [
  ...CHART_TEMPLATE_ORDER.map(chartTemplate),
  {
    id: 'text',
    category: 'text',
    type: 'text',
    label: 'Rich Text',
    description: 'Headings, notes and commentary',
    needs: 'None, you write it in place',
    bestFor: 'Takeaways and context',
  },
  {
    id: 'quote',
    category: 'quote',
    type: 'quote',
    label: 'Quote',
    description: 'A verbatim with its insight and speaker',
    needs: 'A transcript clip or quote',
    bestFor: 'Voice of the patient or HCP',
  },
  {
    id: 'media',
    category: 'media',
    type: 'media',
    label: 'Video',
    description: 'A clip with description and tags',
    needs: 'A video file or link',
    bestFor: 'Interview highlights',
  },
  {
    id: 'section',
    category: 'section',
    type: 'section',
    label: 'Section',
    description: 'Group widgets under one heading',
    needs: 'None',
    bestFor: 'Organizing a long page',
  },
];

// ─── Which categories a builder offers ────────────────────────────────────────

const ENTRY_CATEGORIES: ReadonlyMap<string, AddWidgetCategoryId> = new Map<string, AddWidgetCategoryId>([
  ['chart', 'chart'],
  ['kpi', 'kpi'],
  ['text', 'text'],
  ['quote', 'quote'],
  ['media', 'media'],
  ['section', 'section'],
]);

/** An entry's category: its library kind when it has one, else its type; anything else is "More". */
export function entryCategory(entry: PaletteEntry): AddWidgetCategoryId {
  return ENTRY_CATEGORIES.get(entry.library ?? entry.type) ?? 'more';
}

/** The entries a builder offers, with sections dropped where they can't nest. */
function offeredEntries(entries: readonly PaletteEntry[], hideSections: boolean): PaletteEntry[] {
  return entries.filter((e) => !e.disabled && !(hideSections && entryCategory(e) === 'section'));
}

/**
 * The categories a builder's entries enable. In `new` mode a `chart` entry covers every chart type,
 * so it also enables the KPI Card templates; in `library` mode KPI cards need their own `kpi` entry.
 */
export function enabledCategories(
  entries: readonly PaletteEntry[],
  mode: AddWidgetMode,
  hideSections = false,
): ReadonlySet<AddWidgetCategoryId> {
  const enabled = new Set<AddWidgetCategoryId>();
  for (const entry of offeredEntries(entries, hideSections)) {
    const category = entryCategory(entry);
    enabled.add(category);
    if (mode === 'new' && category === 'chart') enabled.add('kpi');
  }
  return enabled;
}

// ─── Picker items ─────────────────────────────────────────────────────────────

export type PickerItemSource =
  | { kind: 'library'; widget: WidgetInstance }
  | { kind: 'template'; template: WidgetTemplate }
  /** A blank card made from a palette entry (Text, Section, or a type the page has no template for). */
  | { kind: 'entry'; entry: PaletteEntry };

export type PickerItem = {
  id: string;
  category: AddWidgetCategoryId;
  /** Chart type, for the chart/KPI chips. */
  subtype?: string;
  title: string;
  searchText: string;
  source: PickerItemSource;
};

const searchable = (...parts: (string | undefined)[]): string =>
  parts.filter((p): p is string => p !== undefined).join(' ').toLowerCase();

const categoryLabel = (id: AddWidgetCategoryId): string =>
  ADD_WIDGET_CATEGORIES.find((c) => c.id === id)?.label ?? id;

/** The category a saved widget belongs to, or null for kinds the page can't place (rich text, tag summary). */
export function libraryWidgetCategory(widget: WidgetInstance): AddWidgetCategoryId | null {
  switch (widget.type) {
    case SegmentationDashboardWidget.ChartVisualization:
      return isKpiChartType(widget.props?.chart.type) ? 'kpi' : 'chart';
    case SegmentationDashboardWidget.Quote:
      return 'quote';
    case SegmentationDashboardWidget.Media:
      return 'media';
    default:
      return null;
  }
}

function libraryItem(widget: WidgetInstance, category: AddWidgetCategoryId): PickerItem {
  const subtype = widget.type === SegmentationDashboardWidget.ChartVisualization ? widget.props?.chart.type : undefined;
  return {
    id: `library.${widget.id}`,
    category,
    subtype,
    title: widget.title,
    searchText: searchable(widget.title, chartTypeLabel(subtype), categoryLabel(category)),
    source: { kind: 'library', widget },
  };
}

function templateItem(template: WidgetTemplate): PickerItem {
  return {
    id: `template.${template.id}`,
    category: template.category,
    subtype: template.chartType,
    title: template.label,
    searchText: searchable(template.label, template.description, template.needs, template.bestFor),
    source: { kind: 'template', template },
  };
}

function entryItem(entry: PaletteEntry): PickerItem {
  return {
    id: `entry.${entry.type}`,
    category: entryCategory(entry),
    title: entry.label,
    searchText: searchable(entry.label, entry.description),
    source: { kind: 'entry', entry },
  };
}

/**
 * Everything the page lists for a builder. `library` mode shows the project's saved widgets of the
 * enabled kinds, then a blank card for each enabled entry with no library (Text, Section, ...).
 * `new` mode shows one template per enabled type, then a card for each entry the page has no template for.
 */
export function buildPickerItems({
  mode,
  entries,
  libraryWidgets,
  hideSections = false,
}: {
  mode: AddWidgetMode;
  entries: readonly PaletteEntry[];
  libraryWidgets: readonly WidgetInstance[] | undefined;
  hideSections?: boolean;
}): PickerItem[] {
  const enabled = enabledCategories(entries, mode, hideSections);
  const offered = offeredEntries(entries, hideSections);
  const items: PickerItem[] = [];

  if (mode === 'new') {
    items.push(...WIDGET_TEMPLATES.filter((t) => enabled.has(t.category)).map(templateItem));
    items.push(...offered.filter((e) => entryCategory(e) === 'more').map(entryItem));
    return items;
  }

  for (const widget of libraryWidgets ?? []) {
    const category = libraryWidgetCategory(widget);
    if (category && enabled.has(category)) items.push(libraryItem(widget, category));
  }
  items.push(...offered.filter((e) => !e.library).map(entryItem));
  return items;
}

// ─── Recent ───────────────────────────────────────────────────────────────────

/**
 * The remembered picks that can still be offered, in the order remembered. Quotes and media are left
 * out: their cards are wide, and a single clip is rarely added twice, so they don't suit a short row.
 */
export function recentPickerItems(items: readonly PickerItem[], recentIds: readonly string[]): PickerItem[] {
  const byId = new Map(items.map((item) => [item.id, item]));
  const wide = new Set(ADD_WIDGET_CATEGORIES.filter((c) => c.wide).map((c) => c.id));
  return recentIds.flatMap((id) => {
    const item = byId.get(id);
    return item && !wide.has(item.category) ? [item] : [];
  });
}

// ─── Search and filters ───────────────────────────────────────────────────────

export type PickerFilter = {
  query: string;
  category: AddWidgetCategoryId | 'all';
  /** Chart type chip; 'all' for no chip. */
  subtype: string;
};

const matchesQuery = (item: PickerItem, query: string): boolean =>
  query.trim() === '' || item.searchText.includes(query.trim().toLowerCase());

export function filterPickerItems(items: readonly PickerItem[], filter: PickerFilter): PickerItem[] {
  return items.filter(
    (item) =>
      (filter.category === 'all' || item.category === filter.category) &&
      matchesQuery(item, filter.query) &&
      (filter.subtype === 'all' || item.subtype === filter.subtype),
  );
}

/** Rail counts: items per category that match the search, plus the total under 'all'. */
export function countByCategory(
  items: readonly PickerItem[],
  query: string,
): ReadonlyMap<AddWidgetCategoryId | 'all', number> {
  const counts = new Map<AddWidgetCategoryId | 'all', number>([['all', 0]]);
  for (const item of items) {
    if (!matchesQuery(item, query)) continue;
    counts.set('all', (counts.get('all') ?? 0) + 1);
    counts.set(item.category, (counts.get(item.category) ?? 0) + 1);
  }
  return counts;
}

/** The chart types present in a Charts or KPI Card category, for its chips (saved-widget mode only). */
export function chipSubtypes(
  items: readonly PickerItem[],
  category: AddWidgetCategoryId | 'all',
  mode: AddWidgetMode,
): string[] {
  if (mode !== 'library' || (category !== 'chart' && category !== 'kpi')) return [];
  const seen = new Set<string>();
  for (const item of items) {
    if (item.category === category && item.subtype) seen.add(item.subtype);
  }
  return [...seen];
}
